import { isEqual } from 'lodash-es'
import { Pinia, defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import { State, interpret } from 'xstate-v4'
import { routeToFilters } from '~/apps/pu-links/infrastructure/routeToFilters'
import { SearchEngine } from '~/apps/search/domain/engine'
import { getSearchMachine } from '~/apps/search/domain/machine'
import {
  areHierarchiesDifferent,
  redirectOnMachineRedirecting,
  setSearchXkeyHeaders,
} from '~/apps/search/helpers'
import { CountsService } from '~/apps/search/infrastructure/CountsService'
import { FetchCampsitePriceSearchService } from '~/apps/search/infrastructure/FetchCampsitePriceSearchService'
import { FetchCampsiteSearchService } from '~/apps/search/infrastructure/FetchCampsiteSearchService'
import { checkSearchPath } from '~/apps/search/infrastructure/searchRedirectRules'
import { isSearchUrlRoute } from '~/apps/search/isSearchUrlComponent'
import {
  SearchFilters,
  priceSearchCampsiteEndpoint,
  searchCampsiteEndpoint,
} from '~/apps/search/schemas'
import { isAbortError } from '~/utility/isAbortError'
import { waitForInterpreterState } from '~/utility/xstate/waitForInterpreterState'
import { Route } from '~/apps/pu-links/routeHelpers'
import { getLocaleInfo } from '../lang/locales'
import { isLocalStorageAvailable } from '../utility/isLocalStorageAvailable'
import { logger } from '../utility/logger'
import { generateUUID } from '../utility/uuid'
import { resolveQueryToGeoPoint } from '~/apps/search/resolveQueryToGeoPoint'
import { useFacetsStore } from './useFacetsStore'

const storeId = 'searchStorePinia'

// Move getShowMapValue outside the store to make it accessible
export function getShowMapValue(queryParam: string | undefined): boolean {
  // URL param takes precedence
  if (queryParam !== undefined) {
    return queryParam === 'true'
  }
  // Then check localStorage on client
  if (process.client && isLocalStorageAvailable()) {
    const stored = localStorage.getItem('showMap')
    if (stored !== null) {
      return stored === 'true'
    }
  }
  // Default to true
  return true
}

export const useSearchStore = defineStore(storeId, () => {
  // used to track requests in logs
  const requestID = generateUUID()
  const reqId = ref(requestID)
  const searchMachine = getSearchMachine()
  const router = useRouter()
  const route = useRoute()
  const nuxtApp = useNuxtApp()
  const isDesktop = nuxtApp.$isDesktop
  const $gtm = nuxtApp.$gtm
  const langCode = (route.params.lang as string) || 'en-gb'
  const excludeBase = []
  const distanceUnit = getLocaleInfo(langCode).distanceUnit
  const facetsStore = useFacetsStore(usePinia())

  let abortController = new AbortController()
  let countsAbortController = new AbortController()
  const fetchOptions = {
    timeout: process.server ? 10000 : 60000,
  }
  const fetchCampsiteSearchService = new FetchCampsiteSearchService(
    fetchOptions,
  )
  const fetchCampsitePriceSearchService = new FetchCampsitePriceSearchService(
    fetchOptions,
  )

  const engine = ref(0)

  const service = interpret(
    searchMachine.withConfig({
      services: {
        doPriceSearch: ({ filters }) =>
          priceSearchCampsiteEndpoint.validate((args) =>
            fetchCampsitePriceSearchService.search(
              langCode,
              { ...args, engine: engine.value },
              $gtm,
              {
                signal: abortController.signal,
              },
            ),
          )(filters),
        doSearch: async (context, event) => {
          const searchUrl = 'searchUrl' in event ? event.searchUrl : undefined
          const result = await searchCampsiteEndpoint.validate(async (args) => {
            const inner = await fetchCampsiteSearchService.search(
              langCode,
              args,
              $gtm,
              searchUrl,
              {
                signal: abortController.signal,
              },
            )
            return inner
          })(context.filters)
          return result
        },
      },
    }),
  )
  // dated search has a sep counts service
  const countsService = new CountsService({
    logger: $gtm,
  })
  // TODO: can we do away with the engine?
  const searchEngine = new SearchEngine(service)

  // reactive states
  // the search filters used to get a search result
  const searchFilters = ref(routeToFilters(router.currentRoute.value))

  // the search filters used to get a count
  const countSearchFilters = ref<SearchFilters | null>(null)
  const isDated = ref(
    Boolean(routeToFilters(router.currentRoute.value).dates?.arrive),
  )
  // use the ssrState to hydrate on client
  const ssrState = useState('search-store-state', () => '')
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- XState JSON parsing
  const initialState = ssrState.value
    ? JSON.parse(ssrState.value)
    : service.initialState
  let state = State.create(initialState as typeof service.initialState)
  // Use shallowRef for XState context - replaced wholesale on each transition
  // This avoids Vue deeply tracking all campsite/results properties (reduces reactivity overhead)
  const context = shallowRef(state.context)
  const states = ref(state.toStrings())
  // Search results are replaced wholesale, not mutated - use shallowRef
  const searchCounts = shallowRef(state.context.results)
  const initialCounts = shallowRef(state.context.results)
  const recentSearchUrl = ref(router.currentRoute.value.fullPath)

  service.onTransition(searchServiceTransitionHandler).start(state)

  // this is the transition handler, you can't/shouldn't use async/await

  function searchServiceTransitionHandler(newState: any, _event: any) {
    if (process.server) {
      ssrState.value = JSON.stringify(newState)
    }
    // XState creates new references on each transition, so reference comparison
    // is sufficient — no need for deep isEqual checks with shallowRef
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- XState context access
    if (searchFilters.value !== newState.context.filters) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- XState context access
      searchFilters.value = newState.context.filters
    }

    if (state !== newState) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- XState state assignment
      state = newState
    }
    // toStrings() always returns a new array, so compare by value
    // to avoid triggering dependents when state strings haven't changed
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- XState toStrings method
    const newStates: string[] = newState.toStrings()
    if (
      newStates.length !== states.value.length ||
      newStates.some((s: string, i: number) => s !== states.value[i])
    ) {
      states.value = newStates
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- XState context access
    if (context.value !== newState.context) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- XState context access
      context.value = newState.context
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- XState context access
    searchCounts.value = newState.context.results
    // initialCounts is used to determine what initial facets get disabled
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- XState context access
    initialCounts.value = newState.context.results
  }

  // does a search (and a search counts if dated search)
  async function search(
    filters: SearchFilters,
    force = false,
    searchUrl?: string,
  ) {
    // TODO - is this working as intended?
    // looks like different base being set fo the filters means filters are
    // treated as different
    if (!force && isEqual(filters, searchFilters.value)) {
      return false
    }
    if (!filters) {
      return false
    }
    if (!searchUrl) {
      searchUrl = router.currentRoute.value.fullPath
    }
    abort()
    recentSearchUrl.value = searchUrl
    isDated.value = Boolean(filters.dates?.arrive)
    searchFilters.value = filters
    engine.value = (route.name as string)?.includes('searchold')
      ? 1
      : (route.name as string)?.includes('searchnew')
        ? 2
        : 0

    searchEngine.search(filters, searchUrl)
    try {
      await waitForInterpreterState({
        machine: service,
        successStates: ['results', 'redirecting'],
        errorStates: ['error', 'notFound'],
      })
      return true
    } catch (error) {
      console.error('Search error:', error)
      // During SSR, throw 500 so the error page is shown (not an empty results
      // page that could be cached by Varnish/CDN or indexed by Google).
      // The retry: 1 on the fetch calls handles transient 500s — if it also
      // fails, the error is real and should surface.
      if (
        nuxtApp.ssrContext &&
        service.state.matches('error') &&
        !isAbortError(error)
      ) {
        const errorMessage =
          error instanceof Error ? error.message : String(error)
        throw createError({
          statusCode: 500,
          message: `Search error: ${errorMessage}`,
          fatal: false,
          cause: error,
        })
      }
      return false
    }
  }

  // the quick search function
  // called s to make it short
  // does a search with the current route
  async function s() {
    await facetsStore.getFacetGroups(langCode)
    if (nuxtApp.ssrContext && isSearchUrlRoute(route, [], excludeBase)) {
      const pathCheck = checkPath(route)
      if (pathCheck.redirect) {
        if (pathCheck.url) {
          await nuxtApp?.runWithContext(async () => {
            await navigateTo(pathCheck.url, {
              redirectCode: pathCheck.status as number,
            })
          })
        } else {
          nuxtApp.ssrContext.event.node.res.statusCode = pathCheck.status || 404
          throw createError({
            statusCode: pathCheck.status || 404,
            fatal: process.client, // Only set fatal true on client-side
          })
        }
        return
      }
    }
    const rawFilters = routeToFilters(route)
    const filters = await resolveQueryToGeoPoint(rawFilters, langCode)
    await search(filters, true, route.fullPath)

    // Check for notFound state after search and throw 404 during SSR
    if (service.state.matches('notFound') && nuxtApp.ssrContext) {
      throw createError({
        statusCode: 404,
        message: 'Search results not found',
        fatal: false,
      })
    }
    if (
      context.value.filters.hierarchyPath &&
      !context.value.results?.hierarchy
    ) {
      // hierarchy does not exist
      throw createError({
        statusCode: 404,
        fatal: process.client,
        message: 'Hierarchy does not exist',
      })
    }
    if (nuxtApp.ssrContext) {
      const wasRedirected = await handleRedirects(router.currentRoute.value)
      if (wasRedirected) {
        return
      }
      try {
        setSearchXkeyHeaders(context.value, nuxtApp.ssrContext.event.node.res)
      } catch (error) {
        console.error(
          'ERROR: setSearchXkeyHeaders',
          error,
          router.currentRoute.value.fullPath,
          requestID,
        )
      }
      // if (!context.value.results?.campsites?.length) {
      //   // set response to be 404
      //   nuxtApp.ssrContext.res.statusCode = 404
      // }
    }
  }

  // this will just update the counts and not the search
  async function countsSearch(filters: SearchFilters) {
    filters = filterCountsFilter(filters)
    if (isEqual(countSearchFilters.value, filters) && searchCounts.value) {
      return true
    }
    abortCounts()
    countSearchFilters.value = filters
    const innerIsDated = Boolean(filters.dates?.arrive)
    const fetchCounts = innerIsDated
      ? (langCode, filters, options) =>
          countsService.fetchPriceSearchCounts(langCode, filters, options)
      : (langCode, filters, options) =>
          countsService.fetchNonPriceSearchCounts(langCode, filters, options)
    try {
      const searchCountsResults = await fetchCounts(langCode, filters, {
        ...fetchOptions,
        signal: countsAbortController.signal,
      })
      if (searchCountsResults) {
        searchCounts.value = searchCountsResults as typeof searchCounts.value
      }
    } catch (error) {
      console.error('Error fetching counts:', error, requestID)
    }
    return true
  }

  function abort() {
    abortController.abort()
    abortController = new AbortController()
    abortCounts()
  }

  function abortCounts() {
    countsAbortController.abort()
    countsAbortController = new AbortController()
  }

  /**
   * Check if the current URL hierarchy differs from the canonical hierarchy
   * returned by the API. If different, redirect to the correct URL.
   * This handles client-side SPA navigation where the SSR redirect doesn't run.
   */
  async function checkAndRedirectForHierarchyMismatch(route: Route) {
    // Only run on client-side (SPA navigation)
    if (process.server) return

    // Check if hierarchy differs using the same logic as SSR redirects
    if (areHierarchiesDifferent(context.value, searchFilters.value)) {
      const correctUrl = route.fullPath.replace(
        searchFilters.value?.hierarchyPath as string,
        context.value.results?.hierarchy as string,
      )

      logger(
        'checkAndRedirectForHierarchyMismatch',
        'Redirecting from',
        route.fullPath,
        'to',
        correctUrl,
      )

      try {
        // Use navigateTo for client-side redirect (no 301 since we're on the client)
        await navigateTo(correctUrl, { replace: true })
      } catch (error) {
        console.error(
          'ERROR: checkAndRedirectForHierarchyMismatch',
          error,
          requestID,
          route.fullPath,
        )
      }
    }
  }

  async function handleRouteChange(to: Route, from?: Route) {
    const isSearch = isSearchUrlRoute(to, [], excludeBase)
    /**
     * If we're navigating back to homepage, reset data to initial
     * values.
     * This prevents counts from previous searches disabling categories.
     * This prevents subsequent searches from homepage from stalling due to no
     * change in filters.
     */
    if (to.name === 'index') {
      abort()
      searchCounts.value = undefined
      searchFilters.value = routeToFilters(to)
      return
    }
    if (!isSearch) {
      abort()
      return
    }
    const rawFilters = routeToFilters(to)
    const filters = await resolveQueryToGeoPoint(rawFilters, langCode)
    if (
      !isEqual(filters, searchFilters.value) &&
      !to.path.includes('searchedit')
    ) {
      if (isDesktop) {
        // Prevent race conditions when navigating from non-search pages
        const isFromSearchPage = from
          ? isSearchUrlRoute(from, [], excludeBase)
          : false

        if (isFromSearchPage) {
          // Search-to-search: faster navigation
          void search(filters, false, to.fullPath).then(() => {
            // Check for hierarchy mismatch after search completes (client-side SPA navigation)
            void checkAndRedirectForHierarchyMismatch(to)
          })
        } else {
          // Non-search to search: await to prevent race conditions
          try {
            await search(filters, false, to.fullPath)
            // Check for hierarchy mismatch after search completes (client-side SPA navigation)
            await checkAndRedirectForHierarchyMismatch(to)
          } catch (e) {
            console.error('SearchError', e, requestID)
          }
        }
      } else {
        try {
          await search(filters, false, to.fullPath)
          // Check for hierarchy mismatch after search completes (client-side SPA navigation)
          await checkAndRedirectForHierarchyMismatch(to)
        } catch (e) {
          console.error('SearchError', e, requestID)
        }
      }
    }
  }

  if (process.client) {
    // Register the navigation guard when the store is used
    router.beforeEach(async (to, from, next) => {
      // if (isDev)
      //   console.log('searchStore.beforeEach', to.fullPath, from.fullPath)
      try {
        await handleRouteChange(to, from)
        next()
      } catch (error) {
        console.error('Route change failed:', error)
        next() // Continue navigation but log the error
      }
    })
  }

  async function handleRedirects(serverRoute: Route) {
    // Check if current page exceeds total pages
    const currentPage = searchFilters.value.page || 1
    const totalPages = context.value.results?.pagination?.totalPages || 1
    const explicitPageOne = searchFilters.value.page === 1

    if (currentPage > totalPages || explicitPageOne) {
      // Remove page parameter and redirect to first page
      const url = new URL(serverRoute.fullPath, 'http://example.com')
      url.searchParams.delete('page')
      const redirectPath = url.pathname + url.search

      logger(
        'handleRedirects page overflow',
        serverRoute.fullPath,
        redirectPath,
      )
      try {
        await nuxtApp?.runWithContext(async () => {
          await navigateTo(redirectPath, {
            redirectCode: explicitPageOne ? 301 : 302,
          })
        })
      } catch (error) {
        console.error(
          'ERROR: doRedirect page overflow',
          error,
          requestID,
          serverRoute.fullPath,
        )
      }
      return true
    }

    // Existing hierarchy redirect logic
    if (areHierarchiesDifferent(context.value, searchFilters.value)) {
      const url = serverRoute.fullPath.replace(
        searchFilters.value?.hierarchyPath as string,
        context.value.results?.hierarchy as string,
      )
      logger('handleRedirects1', serverRoute.fullPath, url)
      try {
        await nuxtApp?.runWithContext(async () => {
          await navigateTo(url, { redirectCode: 301 })
        })
      } catch (error) {
        console.error(
          'ERROR: doRedirect',
          error,
          requestID,
          serverRoute.fullPath,
        )
      }
      return true
    }

    // Existing SEO redirect logic
    if (states.value.includes('redirecting')) {
      try {
        logger(
          'handleRedirectsSeo',
          context.value.results?.seoRedirect,
          serverRoute.fullPath,
        )
        await redirectOnMachineRedirecting(
          context.value,
          serverRoute.fullPath,
          nuxtApp,
        )
      } catch (error) {
        console.error(
          'ERROR: redirectOnMachineRedirecting',
          error,
          requestID,
          serverRoute.fullPath,
        )
      }
      return true
    }
    return false
  }

  function filterCountsFilter(filters: SearchFilters): SearchFilters {
    return {
      ...filters,
      page: null,
      limit: null,
      base: null,
      sort: null,
    }
  }

  const localizedWithin = computed(() => {
    const distanceWithin = context.value.results?.within
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Add proper typing for within distance units
    return distanceWithin ? distanceWithin[distanceUnit] : ''
  })

  function checkPath(route: Route) {
    const filters = routeToFilters(route)
    return checkSearchPath({
      route,
      filters,
      facetsBySlug: facetsStore.facetsBySlug,
      langCode,
      router,
    })
  }

  // Initialize showMap with just URL param check for SSR consistency
  const showMap = ref(
    route.query.map === undefined ? true : route.query.map === 'true',
  )

  // Move the map functionality into a single object for better organization
  const mapState = {
    showMap,
    toggleMap(show: boolean) {
      logger('toggleMap', show)
      showMap.value = show
      if (process.client && isLocalStorageAvailable()) {
        localStorage.setItem('showMap', show ? 'true' : 'false')
      }
      // if route query needs updating
      if (
        (show && route.query.map === 'false') ||
        (!show && !route.query.map)
      ) {
        void router.replace({
          path: route.path,
          query: show
            ? { ...route.query, map: undefined }
            : { ...route.query, map: 'false' },
        })
      }
    },
    async initializeMapState() {
      const value = getShowMapValue(route.query.map as string | undefined)
      showMap.value = value
      // Add map=false to URL if value is false from localStorage and not already in URL
      if (
        process.client &&
        isLocalStorageAvailable() &&
        !value &&
        route.query.map === undefined &&
        isSearchUrlRoute(route)
      ) {
        logger('searchStore.showMap.query', route.fullPath)
        // Now safe to update route
        await router.replace({
          path: route.path,
          query: { ...route.query, map: 'false' },
        })
      }

      if (process.client && isLocalStorageAvailable()) {
        localStorage.setItem('showMap', value ? 'true' : 'false')
      }
    },
  }

  return {
    context,
    search,
    countsSearch,
    send: service.send,
    states,
    searchCounts,
    initialCounts,
    isDated,
    searchFilters,
    countSearchFilters,
    ssrState,
    filterCountsFilter,
    handleRouteChange,
    reqId,
    localizedWithin,
    s,
    abort,
    showMap: mapState.showMap,
    toggleMap: mapState.toggleMap,
    initializeMapState: mapState.initializeMapState,
    service,
    recentSearchUrl,
  }
})

// Modify the composable to use the new structure
export function useSearchStoreSetup(pinia: Pinia) {
  const searchStore = useSearchStore(pinia)
  return searchStore
}
